import { cloneDeep, get, isArray, isNil, isObject, set } from "lodash";

import { METADATA_KEY, NULL_VALUE, UNDEFINED_VALUE } from "../constants";
import {
  IAuditLogDto,
  IAuditLogProperties,
  IAuditLogPropertyValue,
} from "../dto";
import {
  AuditLogEvents,
  EntityPropertyChanges,
  EntityPropertyTypes,
} from "../enums";
import { FormatDateTimeNormalizedFn } from "../hooks";
import {
  DtoFieldType,
  EntityProperty,
  IAuditLogNormalized,
  IEntityPropertyItemsChange,
  IEntityPropertyPrimitiveValuesBase,
} from "../models";
import {
  applyAuditLogPatch,
  applyAuditLogPatchValue,
  buildAuditLogItemLabels,
  diffAuditLogItemIds,
  getAuditLogIdCollectionConfig,
  getAuditLogIdLabel,
  getAuditLogItemLabel,
  getAuditLogRowDiffVersion,
  hasAuditLogItemIds,
  isAuditLogItemCollectionOrdered,
} from "./audit-log/audit-log-collections.helpers";
import { getEntityPropertySearchTexts } from "./audit-log/audit-log-view.helpers";
import { AUDIT_LOG_ITEMS_LIST_SEPARATOR } from "./audit-log/audit-log.constants";
import type { TAuditLogItemLabels } from "./audit-log/audit-log.models";
import { isISODateStr } from "./date.helpers";
import { hasKeys } from "./objects.helpers";

const FOREIGN_KEY_ID_SUFFIX = "Id";

/** Ends with `Id` but is not a relation (legacy linkage, external identifier). */
const NON_RELATION_ID_FIELDS = new Set(["oldId", "externalId"]);

const isRelationIdFieldKey = (fieldKey: string): boolean =>
  fieldKey.endsWith(FOREIGN_KEY_ID_SUFFIX) &&
  !NON_RELATION_ID_FIELDS.has(fieldKey);

const relationKeyFromForeignKey = (foreignKey: string): string =>
  foreignKey.endsWith(FOREIGN_KEY_ID_SUFFIX)
    ? foreignKey.slice(0, -FOREIGN_KEY_ID_SUFFIX.length)
    : foreignKey;

interface IGetEntityPropertyFromAuditLogParams {
  formatDateTimeNormalized: FormatDateTimeNormalizedFn;
  properties: IAuditLogProperties | null;
  eventName: AuditLogEvents;
  mainProperties: IAuditLogProperties;
  parentPath: string;
  itemLabels?: TAuditLogItemLabels;
  /** The row's diff format, which decides how its arrays are laid over the previous ones. */
  diffVersion?: number;
}

interface IGetEntityPropertyChangesParams {
  formatDateTimeNormalized: FormatDateTimeNormalizedFn;
  mainProperties: IAuditLogProperties;
  path: string;
  valueFromCreate: DtoFieldType | DtoFieldType[];
  value: DtoFieldType | DtoFieldType[];
  skipComparison?: boolean;
}

interface ISimpleLogIdObject {
  id: number;
}

interface ISimpleLogObject extends ISimpleLogIdObject {
  name: string;
}

interface ISimpleLogUserObject extends ISimpleLogIdObject {
  lastName: string;
  firstName: string;
}

const isValidSimpleLogIdObject = (obj: unknown): obj is ISimpleLogIdObject =>
  obj !== null && typeof obj === "object" && "id" in obj;

const isValidSimpleLogObject = (obj: unknown): obj is ISimpleLogObject =>
  isValidSimpleLogIdObject(obj) && "name" in obj;

const isValidSimpleLogUserObject = (
  obj: unknown,
): obj is ISimpleLogUserObject =>
  isValidSimpleLogIdObject(obj) && "lastName" in obj && "firstName" in obj;

/**
 * Converts entity values to readable display format for ID fields
 * Returns formatted string or null if value cannot be processed
 */
const overrideIdFieldByPropertyValue = (
  value: IAuditLogPropertyValue,
): string | null => {
  // Format simple objects as "Name (ID)"
  if (isValidSimpleLogObject(value)) {
    return `${value.name} (${value.id})`;
  }

  // Format user objects as "FirstName LastName (ID)"
  if (isValidSimpleLogUserObject(value)) {
    return `${value.firstName} ${value.lastName} (${value.id})`;
  }

  // Format ID-only objects as just the ID
  if (isValidSimpleLogIdObject(value)) {
    return `${value.id}`;
  }

  return null;
};

const overrideIdFieldsOnShallowRecord = (
  bag: IAuditLogProperties,
): IAuditLogProperties => {
  const result = { ...bag };
  const keysWithId = Object.keys(bag).filter(isRelationIdFieldKey);

  for (const keyWithId of keysWithId) {
    const relatedEntityKey = relationKeyFromForeignKey(keyWithId);
    const overriddenValue = overrideIdFieldByPropertyValue(
      result[relatedEntityKey],
    );

    if (overriddenValue) {
      result[keyWithId] = overriddenValue;
      delete result[relatedEntityKey];
    }
  }

  return result;
};

/**
 * Overrides ID fields with their corresponding entity values for better display
 * Example: userId: 123 becomes userId: "John Doe" if user entity exists
 */
const overrideIdFieldsWithEntityValues = (
  properties: IAuditLogProperties | null,
): IAuditLogProperties => {
  if (!properties || !hasKeys(properties)) {
    return {};
  }

  const result = overrideIdFieldsOnShallowRecord(properties);

  const metadata = result[METADATA_KEY];
  if (isObject(metadata) && !isArray(metadata)) {
    result[METADATA_KEY] = overrideIdFieldsOnShallowRecord(
      metadata as IAuditLogProperties,
    );
  }

  return result;
};

const AUDIT_LOG_WRAPPED_ID_REGEX = /\((\d+)\)$/;
const AUDIT_LOG_NUMERIC_ID_REGEX = /^\d+$/;

/** Extracts a numeric entity id from audit-log display values (e.g. `Label (42)` or plain digits). */
const parseDisplayedAuditLogId = (value: unknown): string | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (typeof value === "number" && Number.isInteger(value)) {
    return String(value);
  }

  const asString = String(value);
  const wrappedId = asString.match(AUDIT_LOG_WRAPPED_ID_REGEX)?.[1];
  if (wrappedId) {
    return wrappedId;
  }

  if (AUDIT_LOG_NUMERIC_ID_REGEX.test(asString)) {
    return asString;
  }

  return undefined;
};

/** True when the leaf segment is a relation id (`*Id`), not `oldId` or `externalId`. */
const isForeignKeyFieldPath = (path: string): boolean => {
  const fieldKey = path.split(".").pop() ?? path;
  return isRelationIdFieldKey(fieldKey);
};

const normalizeEntityValue = ({
  value,
  formatDateTimeNormalized,
}: {
  value: unknown;
  formatDateTimeNormalized: FormatDateTimeNormalizedFn;
}): string => {
  if (isISODateStr(value as string)) {
    return formatDateTimeNormalized(value as TDateISO);
  }
  if (isArray(value) && !value.some(item => isObject(item))) {
    return value.filter(item => item !== null && item !== "").join(",");
  }
  if (isObject(value)) {
    return JSON.stringify(value);
  }
  return String(value);
};

const getEntityPropertyChanges = ({
  formatDateTimeNormalized,
  mainProperties,
  path,
  valueFromCreate,
  value,
  skipComparison,
}: IGetEntityPropertyChangesParams): IEntityPropertyPrimitiveValuesBase | null => {
  if (skipComparison) {
    return {
      changeType: EntityPropertyChanges.Added,
      oldValue: undefined,
      value: normalizeEntityValue({ value, formatDateTimeNormalized }),
    } satisfies IEntityPropertyPrimitiveValuesBase;
  }

  if (isForeignKeyFieldPath(path)) {
    const previousId = parseDisplayedAuditLogId(valueFromCreate);
    const currentId = parseDisplayedAuditLogId(value);
    if (previousId !== undefined && previousId === currentId) {
      return null;
    }
  }

  const oldValueNormalized = normalizeEntityValue({
    value: valueFromCreate,
    formatDateTimeNormalized,
  });

  const valueNormalized = normalizeEntityValue({
    value,
    formatDateTimeNormalized,
  });

  // Both sides are already strings, so a plain comparison is enough.
  if (oldValueNormalized === valueNormalized) {
    return null;
  }

  set(mainProperties, path, valueNormalized);

  return {
    changeType: EntityPropertyChanges.Changed,
    oldValue: oldValueNormalized,
    value: valueNormalized,
  } satisfies IEntityPropertyPrimitiveValuesBase;
};

const buildItemsChangedProperty = ({
  key,
  path,
  before,
  after,
  items,
}: {
  key: string;
  path: string;
  before: string[];
  after: string[];
  items: IEntityPropertyItemsChange;
}): EntityProperty => ({
  key,
  path,
  type: EntityPropertyTypes.ArrayPrimitive,
  changeType: EntityPropertyChanges.ItemsChanged,
  // Names in the plain values keep search and the CSV export readable.
  oldValue: before.join(AUDIT_LOG_ITEMS_LIST_SEPARATOR),
  value: after.join(AUDIT_LOG_ITEMS_LIST_SEPARATOR),
  items,
});

const buildItemsChange = ({
  before,
  after,
  isOrdered,
  toLabel,
}: {
  before: string[];
  after: string[];
  isOrdered: boolean;
  toLabel: (id: string) => string;
}): IEntityPropertyItemsChange | null => {
  const { added, removed, isReordered } = diffAuditLogItemIds({
    before,
    after,
    isOrdered,
  });
  if (!added.length && !removed.length && !isReordered) {
    return null;
  }

  return {
    added: added.map(toLabel),
    removed: removed.map(toLabel),
    ...(isReordered && {
      orderBefore: before.map(toLabel),
      orderAfter: after.map(toLabel),
    }),
  };
};

/** An id list (passengerIds, unitIds, stationIds, ...): only the ids that came or went, plus order for stations. */
const getIdCollectionChanges = ({
  key,
  path,
  previousValue,
  value,
  itemLabels,
}: {
  key: string;
  path: string;
  previousValue: unknown;
  value: unknown[];
  itemLabels?: TAuditLogItemLabels;
}): EntityProperty[] | undefined => {
  const config = getAuditLogIdCollectionConfig({ path, key, values: value });
  if (
    !config ||
    !isArray(previousValue) ||
    !getAuditLogIdCollectionConfig({ path, key, values: previousValue })
  ) {
    return undefined;
  }

  const toLabel = (id: string): string =>
    getAuditLogIdLabel({ id, labelSource: config.labelSource, itemLabels });
  const before = previousValue.map(String);
  const after = value.map(String);
  const items = buildItemsChange({
    before,
    after,
    isOrdered: config.isOrdered,
    toLabel,
  });
  if (!items) {
    return [];
  }

  return [
    buildItemsChangedProperty({
      key,
      path,
      before: before.map(toLabel),
      after: after.map(toLabel),
      items,
    }),
  ];
};

/**
 * Items with ids (passengersInfo, stationsInfo) are matched by id, not by position: a removed or dragged item must
 * not show every later item as changed. Items that came or went are listed by name; kept items show their own field
 * changes. Only stations care about order.
 */
const getItemCollectionChanges = ({
  key,
  path,
  previousValue,
  value,
  eventName,
  mainProperties,
  itemLabels,
  formatDateTimeNormalized,
  diffVersion,
}: {
  key: string;
  path: string;
  previousValue: unknown;
  value: unknown[];
  eventName: AuditLogEvents;
  mainProperties: IAuditLogProperties;
  itemLabels?: TAuditLogItemLabels;
  formatDateTimeNormalized: FormatDateTimeNormalizedFn;
  diffVersion?: number;
}): EntityProperty[] | undefined => {
  if (
    !isArray(previousValue) ||
    !hasAuditLogItemIds(value) ||
    !hasAuditLogItemIds(previousValue)
  ) {
    return undefined;
  }

  const labelById = new Map<string, string>();
  for (const item of [...previousValue, ...value]) {
    labelById.set(
      String(item.id),
      getAuditLogItemLabel(item as Record<string, unknown>),
    );
  }
  const toLabel = (id: string): string => labelById.get(id) ?? id;
  const before = previousValue.map(item => String(item.id));
  const after = value.map(item => String(item.id));

  const previousIndexById = new Map(before.map((id, index) => [id, index]));
  const rows: EntityProperty[][] = [];
  const rowLabels: string[] = [];
  for (const item of value) {
    const id = String(item.id);
    const previousIndex = previousIndexById.get(id);
    if (previousIndex === undefined) {
      continue;
    }
    const row = getEntityPropertyFromAuditLog({
      formatDateTimeNormalized,
      properties: item as IAuditLogProperties,
      eventName,
      mainProperties,
      parentPath: `${path}[${previousIndex}]`,
      itemLabels,
      diffVersion,
    });
    if (row.length) {
      rows.push(row);
      rowLabels.push(toLabel(id));
    }
  }

  const result: EntityProperty[] = [];
  const items = buildItemsChange({
    before,
    after,
    isOrdered: isAuditLogItemCollectionOrdered(path),
    toLabel,
  });
  if (items) {
    result.push(
      buildItemsChangedProperty({
        key,
        path,
        before: before.map(toLabel),
        after: after.map(toLabel),
        items,
      }),
    );
  }
  if (rows.length) {
    result.push({
      key,
      path: `${path}[]`,
      type: EntityPropertyTypes.ArrayObject,
      properties: rows,
      itemLabels: rowLabels,
    });
  }

  return result;
};

const getEntityPropertyFromAuditLog = ({
  formatDateTimeNormalized,
  properties,
  eventName,
  mainProperties,
  parentPath,
  itemLabels,
  diffVersion,
}: IGetEntityPropertyFromAuditLogParams): EntityProperty[] => {
  const isCreatedEvent = eventName === AuditLogEvents.EntityCreated;

  if (!properties || !hasKeys(properties)) {
    return [];
  }

  return Object.entries(properties).flatMap(
    ([key, rawValue]): EntityProperty[] => {
      const path = parentPath ? `${parentPath}.${key}` : key;
      const previousValue: unknown = get(mainProperties as object, path);

      // A null in an update marks a removed key or a cleared column; show what was there.
      if (rawValue === null && !isCreatedEvent) {
        if (isNil(previousValue)) {
          return [];
        }
        return [
          {
            key,
            path,
            type: EntityPropertyTypes.Primitive,
            changeType: EntityPropertyChanges.Removed,
            oldValue: normalizeEntityValue({
              value: previousValue,
              formatDateTimeNormalized,
            }),
            value: "",
          },
        ];
      }

      // An array is compared as the whole array the row produces, read the same way as the replayed state: an
      // index patch (older rows) or an id-keyed item patch is laid over the previous array, anything else replaces it.
      const value =
        !isCreatedEvent && isArray(previousValue)
          ? (applyAuditLogPatchValue({
              current: previousValue,
              patch: rawValue,
              diffVersion,
            }) as IAuditLogPropertyValue)
          : rawValue;

      if (isArray(value)) {
        const hasObjectItems = value.some(item => isObject(item));

        if (!isCreatedEvent) {
          const collectionChanges = hasObjectItems
            ? getItemCollectionChanges({
                key,
                path,
                previousValue,
                value,
                eventName,
                mainProperties,
                itemLabels,
                formatDateTimeNormalized,
                diffVersion,
              })
            : getIdCollectionChanges({
                key,
                path,
                previousValue,
                value,
                itemLabels,
              });
          if (collectionChanges) {
            return collectionChanges;
          }
        }

        if (hasObjectItems) {
          const rowProperties = (
            value as IAuditLogProperties[] | IAuditLogPropertyValue[]
          ).map((childProperty, index) =>
            getEntityPropertyFromAuditLog({
              formatDateTimeNormalized,
              properties: childProperty as IAuditLogProperties,
              eventName,
              mainProperties,
              parentPath: `${path}[${index}]`,
              itemLabels,
              diffVersion,
            }),
          );
          const nonEmptyRows = rowProperties.filter(row => row.length > 0);
          if (!nonEmptyRows.length) {
            return [];
          }
          return [
            {
              key,
              path,
              type: EntityPropertyTypes.ArrayObject,
              properties: nonEmptyRows,
            },
          ];
        }

        const arrayChanges = getEntityPropertyChanges({
          formatDateTimeNormalized,
          mainProperties,
          path,
          valueFromCreate: previousValue as DtoFieldType[],
          value: value as DtoFieldType[],
          skipComparison: isCreatedEvent,
        });
        if (!arrayChanges) {
          return [];
        }
        return [
          {
            key,
            path,
            type: EntityPropertyTypes.ArrayPrimitive,
            ...arrayChanges,
          },
        ];
      }

      if (isObject(value)) {
        const nested = getEntityPropertyFromAuditLog({
          formatDateTimeNormalized,
          properties: value as IAuditLogProperties,
          eventName,
          mainProperties,
          parentPath: path,
          itemLabels,
          diffVersion,
        });
        if (!nested.length) {
          return [];
        }
        return [
          {
            key,
            path,
            type: EntityPropertyTypes.Object,
            properties: nested,
          },
        ];
      }

      const primitiveChanges = getEntityPropertyChanges({
        formatDateTimeNormalized,
        mainProperties,
        path,
        valueFromCreate: previousValue as DtoFieldType,
        value: value as DtoFieldType,
        skipComparison: isCreatedEvent,
      });
      if (!primitiveChanges) {
        return [];
      }
      return [
        {
          key,
          path,
          type: EntityPropertyTypes.Primitive,
          ...primitiveChanges,
        },
      ];
    },
  );
};

/** Oldest first. Ids grow with insertion, so they order rows even when two share a createdAt. */
const sortAuditLogsOldToNew = (logs: IAuditLogDto[]): IAuditLogDto[] =>
  [...logs].sort((a, b) => a.id - b.id);

export const normalizeAuditLogs = ({
  auditLogs,
  formatDateTimeNormalized,
  areaNameById,
}: {
  auditLogs: IAuditLogDto[];
  formatDateTimeNormalized: FormatDateTimeNormalizedFn;
  areaNameById?: Map<number, string>;
}): IAuditLogNormalized[] => {
  if (!auditLogs?.length) {
    return [];
  }

  const sortedAuditLogs = sortAuditLogsOldToNew(auditLogs);

  const createdAuditLog = sortedAuditLogs.find(
    auditLog => auditLog.eventName === AuditLogEvents.EntityCreated,
  );
  const mainProperties = cloneDeep(
    overrideIdFieldsWithEntityValues(createdAuditLog?.properties ?? {}),
  );

  let fullPropertiesOriginal = cloneDeep(createdAuditLog?.properties ?? {});

  const result: IAuditLogNormalized[] = [];

  for (const auditLog of sortedAuditLogs) {
    const currentProperties = auditLog.properties ?? {};
    const isCreatedEvent = auditLog.eventName === AuditLogEvents.EntityCreated;
    const diffVersion = getAuditLogRowDiffVersion(auditLog);
    const comparisonBaseline = isCreatedEvent
      ? mainProperties
      : overrideIdFieldsWithEntityValues(cloneDeep(fullPropertiesOriginal));

    // Each update row holds only what changed: arrays whole, jsonb keys merged, removed jsonb keys as null.
    if (!isCreatedEvent) {
      fullPropertiesOriginal = applyAuditLogPatch({
        state: fullPropertiesOriginal,
        patch: currentProperties,
        diffVersion,
      });
    }

    const entityProperties = getEntityPropertyFromAuditLog({
      formatDateTimeNormalized,
      properties: overrideIdFieldsWithEntityValues(auditLog.properties),
      eventName: auditLog.eventName,
      mainProperties: comparisonBaseline,
      parentPath: "",
      diffVersion,
      // Removed items are named from the state before the row, added ones from the state after it.
      itemLabels: buildAuditLogItemLabels({
        states: [comparisonBaseline, fullPropertiesOriginal],
        areaNameById,
      }),
    });

    result.push({
      ...auditLog,
      entityProperties,
      fullProperties: cloneDeep(fullPropertiesOriginal),
    });
  }

  return result;
};

const includesSearch = ({
  text,
  search,
}: {
  text: string | undefined;
  search: string;
}): boolean => !!text?.toLowerCase().includes(search);

/** Who made the change, on what, and when: a match here keeps the whole row. */
const matchesAuditLogMainFields = ({
  auditLog,
  search,
}: {
  auditLog: IAuditLogNormalized;
  search: string;
}): boolean =>
  [
    auditLog.causerName,
    auditLog.subjectName,
    auditLog.entity,
    auditLog.service,
    auditLog.createdAt,
  ].some(text => includesSearch({ text, search }));

/** Fields whose key or value contains `search` (lower-cased); objects and item rows keep only their matching children. */
const filterEntityProperties = ({
  entityProperties,
  search,
}: {
  entityProperties: EntityProperty[];
  search: string;
}): EntityProperty[] =>
  entityProperties.flatMap((entityProperty): EntityProperty[] => {
    switch (entityProperty.type) {
      case EntityPropertyTypes.Primitive:
      case EntityPropertyTypes.ArrayPrimitive: {
        const isMatch = [
          entityProperty.key,
          ...getEntityPropertySearchTexts(entityProperty),
        ].some(text => includesSearch({ text, search }));
        return isMatch ? [entityProperty] : [];
      }
      case EntityPropertyTypes.Object: {
        const properties = filterEntityProperties({
          entityProperties: entityProperty.properties,
          search,
        });
        return properties.length ? [{ ...entityProperty, properties }] : [];
      }
      case EntityPropertyTypes.ArrayObject: {
        const rows = entityProperty.properties.flatMap((row, index) => {
          const itemLabel = entityProperty.itemLabels?.[index];
          // A match on the item's own name (a station or passenger) keeps the whole row.
          const properties = includesSearch({ text: itemLabel, search })
            ? row
            : filterEntityProperties({ entityProperties: row, search });
          return properties.length ? [{ properties, itemLabel }] : [];
        });
        if (!rows.length) {
          return [];
        }
        return [
          {
            ...entityProperty,
            properties: rows.map(row => row.properties),
            itemLabels: entityProperty.itemLabels
              ? rows.map(row => row.itemLabel ?? "")
              : undefined,
          },
        ];
      }
    }
  });

export const filterAuditLogsBySearch = (
  auditLogs: IAuditLogNormalized[],
  search: string,
): IAuditLogNormalized[] => {
  if (!search) {
    return auditLogs;
  }

  const searchLower = search.toLowerCase();

  return (auditLogs ?? []).flatMap(auditLog => {
    const entityProperties = filterEntityProperties({
      entityProperties: auditLog.entityProperties ?? [],
      search: searchLower,
    });
    if (entityProperties.length) {
      return [{ ...auditLog, entityProperties }];
    }
    return matchesAuditLogMainFields({ auditLog, search: searchLower })
      ? [auditLog]
      : [];
  });
};

const specialNilValues = new Set([
  undefined,
  null,
  UNDEFINED_VALUE,
  NULL_VALUE,
]);

export const checkPropertyValueNill = (
  value: string | undefined | null,
): boolean => {
  return specialNilValues.has(value);
};
